import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PageHeader, DetailCard, LoadingState } from '../components';
import { useBusinessSettings, useUpdateBusinessSettings } from '../hooks/useBusinessSettings';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, COSTING_METHODS, OVERSELL_BEHAVIORS, humanize } from '../constants';

export default function Settings() {
  const history = useHistory();
  const businessUuid = useBusinessUuid();
  const { data: settings, isLoading } = useBusinessSettings();
  const updateSettings = useUpdateBusinessSettings();

  const [costingMethod, setCostingMethod] = useState('fifo');
  const [defMinStock, setDefMinStock] = useState('0');
  const [defMaxStock, setDefMaxStock] = useState('0');
  const [reorderPoint, setReorderPoint] = useState('0');
  const [reorderQty, setReorderQty] = useState('0');
  const [lowStockEnabled, setLowStockEnabled] = useState(true);
  const [lowStockThreshold, setLowStockThreshold] = useState('10');
  const [expiryAlertDays, setExpiryAlertDays] = useState('7');
  const [enableLots, setEnableLots] = useState(false);
  const [allowOversell, setAllowOversell] = useState(false);
  const [oversellBehavior, setOversellBehavior] = useState('block');
  const [maxOversell, setMaxOversell] = useState('0');
  const [reservationExpiry, setReservationExpiry] = useState('24');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (settings) {
      setCostingMethod(settings.costingMethod);
      setDefMinStock(String(settings.defaultMinStockLevel ?? 0));
      setDefMaxStock(String(settings.defaultMaxStockLevel ?? 0));
      setReorderPoint(String(settings.defaultReorderPoint ?? 0));
      setReorderQty(String(settings.defaultReorderQuantity ?? 0));
      setLowStockEnabled(!!settings.lowStockAlertEnabled);
      setLowStockThreshold(String(settings.lowStockAlertThreshold ?? 10));
      setExpiryAlertDays(String(settings.expiryAlertDays ?? 7));
      setEnableLots(!!settings.enableLotTracking);
      setAllowOversell(!!settings.allowOversell);
      setOversellBehavior(settings.oversellBehavior ?? 'block');
      setMaxOversell(String(settings.maxOversellQuantity ?? 0));
      setReservationExpiry(String(settings.reservationExpiryHours ?? 24));
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        costingMethod,
        defaultMinStockLevel: parseInt(defMinStock, 10) || 0,
        defaultMaxStockLevel: parseInt(defMaxStock, 10) || 0,
        defaultReorderPoint: parseInt(reorderPoint, 10) || 0,
        defaultReorderQuantity: parseInt(reorderQty, 10) || 0,
        lowStockAlertEnabled: lowStockEnabled,
        lowStockAlertThreshold: parseInt(lowStockThreshold, 10) || 10,
        expiryAlertDays: parseInt(expiryAlertDays, 10) || 7,
        enableLotTracking: enableLots,
        allowOversell,
        oversellBehavior,
        maxOversellQuantity: parseInt(maxOversell, 10) || 0,
        reservationExpiryHours: parseInt(reservationExpiry, 10) || 24,
      });
      setToast({ severity: 'success', message: 'Settings saved' });
    } catch (e) {
      setToast({ severity: 'error', message: e?.response?.data?.message || 'Something went wrong' });
    }
  };

  if (isLoading) return <LoadingState />;

  return (
    <>
      <PageHeader title="Settings" subtitle={`Business: ${businessUuid}`} />
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <DetailCard title="Inventory Defaults">
            <Stack spacing={2}>
              <TextField select label="Costing Method" value={costingMethod} onChange={(e) => setCostingMethod(e.target.value)} size="small">
                {COSTING_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>{m.toUpperCase()}</MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={2}>
                <TextField label="Default Min Stock" type="number" value={defMinStock} onChange={(e) => setDefMinStock(e.target.value)} size="small" fullWidth />
                <TextField label="Default Max Stock" type="number" value={defMaxStock} onChange={(e) => setDefMaxStock(e.target.value)} size="small" fullWidth />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Reorder Point" type="number" value={reorderPoint} onChange={(e) => setReorderPoint(e.target.value)} size="small" fullWidth />
                <TextField label="Reorder Quantity" type="number" value={reorderQty} onChange={(e) => setReorderQty(e.target.value)} size="small" fullWidth />
              </Stack>

              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.gray600 }}>Alerts</Typography>
              <FormControlLabel
                control={<Switch checked={lowStockEnabled} onChange={(e) => setLowStockEnabled(e.target.checked)} />}
                label="Low Stock Alerts"
              />
              <Stack direction="row" spacing={2}>
                <TextField label="Low Stock Threshold" type="number" value={lowStockThreshold} onChange={(e) => setLowStockThreshold(e.target.value)} size="small" fullWidth />
                <TextField label="Expiry Alert (days)" type="number" value={expiryAlertDays} onChange={(e) => setExpiryAlertDays(e.target.value)} size="small" fullWidth />
              </Stack>

              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.gray600 }}>Lot Tracking &amp; Oversell</Typography>
              <FormControlLabel
                control={<Switch checked={enableLots} onChange={(e) => setEnableLots(e.target.checked)} />}
                label="Enable Lot Tracking"
              />
              <FormControlLabel
                control={<Switch checked={allowOversell} onChange={(e) => setAllowOversell(e.target.checked)} />}
                label="Allow Oversell"
              />
              <TextField select label="Oversell Behavior" value={oversellBehavior} onChange={(e) => setOversellBehavior(e.target.value)} size="small">
                {OVERSELL_BEHAVIORS.map((b) => (
                  <MenuItem key={b} value={b}>{humanize(b)}</MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={2}>
                <TextField label="Max Oversell Qty" type="number" value={maxOversell} onChange={(e) => setMaxOversell(e.target.value)} size="small" fullWidth />
                <TextField label="Reservation Expiry (hours)" type="number" value={reservationExpiry} onChange={(e) => setReservationExpiry(e.target.value)} size="small" fullWidth />
              </Stack>

              <Button variant="contained" onClick={handleSave} disabled={updateSettings.isPending} sx={{ backgroundColor: colors.primary, textTransform: 'none' }}>
                {updateSettings.isPending ? 'Saving...' : 'Save Settings'}
              </Button>
            </Stack>
          </DetailCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <DetailCard title="More">
            <Box
              onClick={() => history.push('/inventory-management/settings/units-of-measure')}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, cursor: 'pointer' }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ScaleOutlinedIcon sx={{ color: colors.gray600 }} />
                <Typography variant="body1">Units of Measure</Typography>
              </Stack>
              <ChevronRightIcon sx={{ color: colors.gray400 }} />
            </Box>
          </DetailCard>
        </Grid>
      </Grid>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
